import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {startUserLogIn, loginWithPassword} from 'utils/auth/login';

import {Alert, Box, Stack, TextField, Button, Collapse, Link, Typography} from 'views/components';
import {MailOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';
import EditInputAdornment from './components/EditInputAdornment';

const EmailBox = (props) => {
  const {
    challengeName,
    setChallengeName,
    onResetPassword,
    onForgotPassword,
    onOtpLogin,

    userReload,
    statusProgressWrap: progressWrap,
    statusClear,
  } = props;

  const passwordFieldRef = React.useRef();

  const [email, setEmail] = React.useState('');
  const [emailFailed, setEmailFailed] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState(false);

  const isEmailValid = /^\S+@\S+\.\S+$/.test(email);
  const isPasswordValid = Boolean(password.length >= 6);

  const isUsingPasswordLogin = challengeName === 'PASSWORD';

  const isButtonDisabled = (!isUsingPasswordLogin && !isEmailValid) || (isUsingPasswordLogin && !isPasswordValid);

  React.useEffect(() => {
    if (isEmailValid) {
      setEmailFailed(false);
    }
  }, [isEmailValid]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isUsingPasswordLogin) {
      if (!isEmailValid || !isPasswordValid) {
        return;
      }

      progressWrap(async () => {
        setLoginError(false);
        setLoading(true);
        try {
          const e = await loginWithPassword(email, password);
          if (e.status === 'NEW_PASSWORD_REQUIRED') {
            onResetPassword(e);
          } else {
            userReload();
          }
          statusClear();
        } finally {
          setLoading(false);
        }
      }, {
        onErrorMessage: () => {
          setLoginError(true);
        },
      });
    } else {
      if (!isEmailValid) {
        setEmailFailed(true);
        return;
      }

      progressWrap(async () => {
        setLoading(true);
        try {
          const challengeName = await startUserLogIn(email);

          setChallengeName(challengeName);
          if (challengeName === 'OTP') {
            onOtpLogin(email);
          }
        } catch (e) {
          setChallengeName('ERROR');
        }
        setLoading(false);
      });
    }
  };

  const resetToStart = () => {
    setLoginError(false);
    setPassword('');
    setChallengeName('START');
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      autoComplete='on'
      noValidate
    >
      <Stack
        alignItems="center"
        spacing={{xs: 2, sm: 4}}
      >
        <MailOutlined
          sx={{
            height: {
              xs: 36, sm: 52,
            },
            width: {
              xs: 36, sm: 52,
            },
            color: 'text.contrast',
          }}
        />
        {/* Titles */}
        <Stack spacing={1} alignItems="center">
          <Typography variant="h4" fontWeight="normal" align="center" color="text.primary">
            <Translate expression={isUsingPasswordLogin ? 'auth.EmailBox.PasswordTitle' : 'auth.EmailBox.EmailTitle'} />
          </Typography>
          <Typography variant="body1" color="text.primary" align="center">
            <Translate expression={isUsingPasswordLogin ? 'auth.EmailBox.PasswordDescription' : 'auth.EmailBox.EmailDescription'} />
          </Typography>
        </Stack>
        <TextField
          label={translate('auth.Signup.UserAuth.Email')}
          type='email'
          name='email'
          autoComplete='email'
          fullWidth
          value={email}
          onChange={setEmail}
          InputLabelProps={{shrink: true}}
          disabled={isUsingPasswordLogin}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
          helperText={emailFailed ? translate('auth.Signup.UserAuth.Errors.Email') : ''}
          size='medium'
          slotProps={{
            input: {
              endAdornment: <EditInputAdornment
                in={isUsingPasswordLogin}
                onClick={resetToStart}
              />,
            },
          }}
        />
        <Collapse
          in={isUsingPasswordLogin}
          onEntered={() => passwordFieldRef.current.focus()}
          unmountOnExit
          sx={{
            width: '100%',
          }}
        >
          <TextField
            inputRef={passwordFieldRef}
            label={translate('auth.Signup.UserAuth.Password')}
            type='password'
            name='password'
            autoComplete={isUsingPasswordLogin ? 'current-password' : ''}
            fullWidth
            value={password}
            onChange={setPassword}
            InputLabelProps={{shrink: true}}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            size='medium'
          />
        </Collapse>
        <Stack spacing={2} alignItems="center" width="100%">
          <Button
            type='submit'
            variant={isButtonDisabled ? 'outlined' : 'contained'}
            fullWidth
            disabled={isButtonDisabled}
            loading={loading}
            sx={{
              'py': 2,
              'borderRadius': 2,
              'textTransform': 'none',
              'backgroundColor': isButtonDisabled ? 'color.white' : '#DA7955',
              'color': isButtonDisabled ? '#2D2925B8' : 'color.white',
            }}
          >
            <Translate expression='auth.Signup.UserAuth.Login' />
          </Button>
          {
            loginError &&
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  width: '100%',
                }}
              >
                <Translate expression='auth.NotAuthorizedException' />
              </Alert>
          }
        </Stack>
        {
          !isUsingPasswordLogin &&
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={0.5} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              <Translate expression="auth.EmailBox.LinkPrompt" />
            </Typography>
            <Link
              component="button"
              variant="body1"
              type="button"
              href={'https://calendly.com/daniel-carequilt/care-plan-call-with-daniel'}
              sx={{
                cursor: 'pointer',
              }}
            >
              <Translate expression="auth.EmailBox.LinkText" />
            </Link>
          </Stack>
        }
        <Collapse in={isUsingPasswordLogin} >
          <Link
            variant="body1"
            onClick={() => onForgotPassword(email)}
          >
            <Translate expression='auth.Signup.UserAuth.ForgotPassword.Create' />
          </Link>
        </Collapse>
      </Stack>
    </Box>
  );
};

EmailBox.propTypes = {
  challengeName: PropTypes.string.isRequired,
  setChallengeName: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  onForgotPassword: PropTypes.func.isRequired,
  onOtpLogin: PropTypes.func.isRequired,

  statusProgressWrap: PropTypes.func.isRequired,
  statusClear: PropTypes.func.isRequired,
  userReload: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, dispatch)(React.memo(EmailBox));
